import { useEffect } from 'react';
import type { BoardDoc } from '../doc/BoardDoc';
import { acquireBoard, releaseBoard } from '../doc/docCache';
import { clearThumbnail, uploadThumbnail } from '../sync/boardsApi';
import { boardBounds, itemsIn, rasterize } from './export/rasterize';

/**
 * Dashboard thumbnails. An editor's browser renders the whole board small (the PNG export
 * pipeline at a tiny scale) and uploads it: after a quiet spell following local edits, when the
 * tab goes to the background, and when the toolbar's home button is used (flushThumbnails).
 */

const TARGET_PX = 640;
const QUIET_MS = 8000;
const MIN_INTERVAL_MS = 30000;
const FLUSH_TIMEOUT_MS = 4000;

export async function renderThumbnail(board: BoardDoc): Promise<Blob | null> {
  const bounds = boardBounds(board);
  if (!bounds || bounds.w <= 0 || bounds.h <= 0) return null;
  const longest = Math.max(bounds.w, bounds.h);
  const padding = longest * 0.03;
  const scale = Math.min(2, TARGET_PX / (longest + padding * 2));
  const canvas = await rasterize(board, itemsIn(board, bounds), bounds, { scale, background: background(board), padding });
  // Browsers without WebP encoding (Safari) hand back a PNG; the server sniffs the type.
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/webp', 0.8));
}

function background(board: BoardDoc): string {
  const bg = board.meta.get('bg');
  if (typeof bg === 'string' && bg) return bg;
  const pane = document.querySelector('.pane');
  const fromPane = pane ? getComputedStyle(pane).getPropertyValue('--canvas-bg').trim() : '';
  return fromPane || getComputedStyle(document.documentElement).getPropertyValue('--canvas-bg').trim() || '#cfcfcf';
}

/** Capture jobs of the open boards, so leaving a board can push its thumbnail first. */
const pending = new Map<string, () => Promise<void>>();

/** Uploads pending thumbnails, waiting at most a few seconds so navigation never hangs. */
export async function flushThumbnails(): Promise<void> {
  const jobs = [...pending.values()].map((run) => run());
  if (!jobs.length) return;
  await Promise.race([Promise.allSettled(jobs), new Promise((r) => setTimeout(r, FLUSH_TIMEOUT_MS))]);
}

/**
 * Keeps the server thumbnail of a board fresh while it is open. `needsInitial` is true when the
 * board has no thumbnail yet, so the first sync alone (no local edit) produces one.
 */
export function useThumbnailSync(board: BoardDoc | null, enabled: boolean, needsInitial: boolean): void {
  useEffect(() => {
    if (!board || !enabled) return;
    const { boardId } = board;
    let initial = needsInitial;
    let hasThumb = !needsInitial;
    let dirty = false;
    let busy = false;
    let lastUpload = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const run = async (): Promise<void> => {
      if (busy || !dirty) return;
      busy = true;
      dirty = false;
      // hold a reference so the document survives a page change while rendering
      const held = acquireBoard(boardId, true);
      try {
        const blob = await renderThumbnail(board);
        if (blob) {
          await uploadThumbnail(boardId, blob);
          hasThumb = true;
          initial = false;
        } else if (hasThumb) {
          await clearThumbnail(boardId);
          hasThumb = false;
        }
        lastUpload = Date.now();
      } catch (e) {
        console.warn('[thumbnail] not updated', e);
        dirty = true;
      } finally {
        busy = false;
        releaseBoard(held);
      }
    };

    const schedule = () => {
      clearTimeout(timer);
      const wait = Math.max(QUIET_MS, lastUpload + MIN_INTERVAL_MS - Date.now());
      timer = setTimeout(() => void run(), wait);
    };
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      const fromElsewhere = origin === board.provider || origin === board.persistence;
      if (fromElsewhere && !initial) return;
      dirty = true;
      schedule();
    };
    const onVisibility = () => {
      if (document.hidden && dirty) {
        clearTimeout(timer);
        void run();
      }
    };

    board.doc.on('update', onUpdate);
    document.addEventListener('visibilitychange', onVisibility);
    pending.set(boardId, run);
    return () => {
      clearTimeout(timer);
      board.doc.off('update', onUpdate);
      document.removeEventListener('visibilitychange', onVisibility);
      if (pending.get(boardId) === run) pending.delete(boardId);
    };
  }, [board, enabled, needsInitial]);
}
